<template>
  <Layout>
    <div class="dash-root">
      <div v-if="userData.permissions['view_dashboard'] == 1">

        <!-- Header -->
        <div class="dash-header">
          <h5 class="dash-title"><i class="mdi mdi-speedometer"></i> Dashboard</h5>

          <div class="fy-pill" v-if="userData.permissions['view_financial_years'] == 1">
            <span class="fy-label"><i class="mdi mdi-calendar-outline"></i> Financial Year</span>
            <b-dropdown @show="toggleArrowDirection" @hide="toggleArrowDirection" size="sm"
              id="dropdown-left dropup" variant="primary" class="fy-dropdown" :class="{ dropup: arrow_direction }">
              <template #button-content>
                <div v-if="loader" class="fy-loading">
                  <b-spinner small></b-spinner>
                </div>
                <div v-else class="d-flex align-items-center justify-content-center">
                  {{ financialYear }}
                  <i class="fs-5 ms-2" :class="arrowDirection ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
                </div>
              </template>
              <b-dropdown-item v-for="(year, index) in financial_years" :key="index"
                @click.prevent="selectFinancialYear(year.id)">{{ year.name }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <div class="fy-pill fy-pill--static" v-else>
            <span class="fy-label"><i class="mdi mdi-calendar-outline"></i> Financial Year</span>
            <span class="fy-value">{{ financialYear }}</span>
          </div>
        </div>

        <!-- KPI Cards -->
        <div class="kpi-grid">
          <div class="kpi-card" v-for="(card, index) in activeCards" :key="index"
            @click="$router.push(card.link)">
            <span class="kpi-card__accent" :style="{ background: cardColor(index) }"></span>
            <div class="kpi-card__top">
              <span class="kpi-card__label">{{ card.name }}</span>
              <span class="kpi-card__icon" :style="{ color: cardColor(index), background: cardColorSoft(index) }">
                <i :class="card.icon"></i>
              </span>
            </div>
            <div class="kpi-card__value">{{ commas(card.number) }}</div>
          </div>
        </div>

        <!-- Charts -->
        <div class="chart-grid">
          <div class="chart-card">
            <div class="chart-card__header">
              <span class="chart-card__dot" style="background:#0E7C66"></span>
              <h6 class="chart-card__title">{{ month }} Savings</h6>
            </div>
            <apexchart class="apex-charts" type="bar" height="300px" :options="this.monthlysavings.chartOptions"
              :series="monthlysavings.series"></apexchart>
          </div>

          <div class="chart-card">
            <div class="chart-card__header">
              <span class="chart-card__dot" style="background:#2952E3"></span>
              <h6 class="chart-card__title">{{ year }} Loans Analysis</h6>
            </div>
            <apexchart class="apex-charts" type="line" height="300" :options="monthlyloans.chartOptions"
              :series="monthlyloans.series"></apexchart>
          </div>
        </div>
      </div>

      <!-- No permission state -->
      <div v-else class="no-access">
        <div class="no-access__icon"><i class="mdi mdi-lock-outline"></i></div>
        <img src="@/assets/images/profile-img.png" alt class="no-access__img" />
        <div class="no-access__title">No dashboard access</div>
        <div class="no-access__subtitle">You don't have permission to view dashboard items. Contact your
          administrator if you believe this is a mistake.</div>
      </div>
    </div>

    <!-- Updates modal -->
    <b-modal id="updates" size="xl" hide-footer hide-header no-close-on-backdrop no-close-on-esc>
      <div class="updates-modal">
        <div class="updates-modal__title">Latest Updates</div>

        <div class="updates-modal__body">
          <button class="updates-nav updates-nav--prev" :class="{ 'is-disabled': update_number == 1 }"
            @click.prevent="previousUpdate" aria-label="Previous">
            <i class="mdi mdi-chevron-left"></i>
          </button>

          <div class="updates-modal__frame">
            <img v-if="update_number == 1" src="@/assets/images/updates/credit_score/1.webp" alt="Update 1">
            <img v-if="update_number == 2" src="@/assets/images/updates/credit_score/2.webp" alt="Update 2">
            <img v-if="update_number == 3" src="@/assets/images/updates/credit_score/3.webp" alt="Update 3">
            <img v-if="update_number == 4" src="@/assets/images/updates/credit_score/4.webp" alt="Update 4">
            <img v-if="update_number == 5" src="@/assets/images/updates/credit_score/5.webp" alt="Update 5">
            <img v-if="update_number == 6" src="@/assets/images/updates/credit_score/6.webp" alt="Update 6">
            <img v-if="update_number == 7" src="@/assets/images/updates/credit_score/7.webp" alt="Update 7">
            <img v-if="update_number == 8" src="@/assets/images/updates/credit_score/8.webp" alt="Update 8">
          </div>

          <button class="updates-nav updates-nav--next" :class="{ 'is-disabled': update_number == update_total }"
            @click.prevent="nextUpdate" aria-label="Next">
            <i class="mdi mdi-chevron-right"></i>
          </button>
        </div>

        <div class="updates-modal__dots">
          <span v-for="n in update_total" :key="n" class="updates-dot" :class="{ 'is-active': n == update_number }"></span>
        </div>

        <div class="updates-modal__footer">
          <button class="btn-primary-pill" @click.prevent="seenUpdates">Got it</button>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import axios from "axios";
import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import {
  memberMethods, memberComputed, savingMethods, savingComputed,
  shareMethods, shareComputed, withdrawMethods, withdrawComputed,
  runningloanMethods, runningloanComputed, loandisbursementMethods, loandisbursementComputed
} from "@/state/helpers";

export default {
  page: {
    title: "Dashboard",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout, },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")),
      active_branch: localStorage.getItem('active_branch'),
      financialYear: (localStorage.getItem("financialyear")),
      month: "", year: "",
      arrow_direction: false,
      financial_years: [],
      members: '0', savings: '0', withdraws: '0', withdrawcharges: '0', shares: '0', capital: '0',
      loans: '0', loanstaken: '0', loanspaid: '0', interest: '0', arrears: '0', returns: '0',
      yearlyloans: [],
      yearlysavings: [],
      loader: false,
      update_number: 1,
      update_total: 8,

      // Category accent palette used to color-code KPI cards by index (see cardColor/cardColorSoft)
      cardPalette: ['#2952E3', '#0E7C66', '#7C3AED', '#C2540A', '#0E7C66', '#2952E3', '#C2540A', '#2952E3', '#0E7C66', '#7C3AED', '#C2540A', '#0E7C66'],

      monthlyloans: {
        series: [{ name: "Loans", data: [], },],
        chartOptions: {
          noData: { text: 'Please Wait...' },
          colors: ['#2952E3', '#0E7C66'],
          legend: { show: false },
          chart: { toolbar: { show: false }, fontFamily: 'Inter, sans-serif', foreColor: '#64748B' },
          grid: { borderColor: '#EEF1F5', strokeDashArray: 4 },
          stroke: { curve: 'smooth', width: 3 },
          dataLabels: { enabled: false },
        },
      },

      monthlysavings: {
        series: [{ name: "Savings", data: [] }],
        chartOptions: {
          noData: { text: 'Please Wait...' },
          fill: { colors: ['#0E7C66', '#2952E3'] },
          colors: ['#0E7C66', '#2952E3'],
          legend: { show: false },
          dataLabels: { enabled: false },
          chart: { toolbar: { show: false }, fontFamily: 'Inter, sans-serif', foreColor: '#64748B' },
          grid: { borderColor: '#EEF1F5', strokeDashArray: 4 },
          plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
        },
      },
      card_hover: false,
      card_number: '',
      cards: [
        { name: 'Members', number: 0, icon: 'mdi mdi-account-multiple', link: '/viewmembers', visible: JSON.parse(localStorage.getItem('user')).permissions['view_members'] },
        { name: 'Savings', number: 0, icon: 'mdi mdi-google-circles-group', link: '/savings', visible: JSON.parse(localStorage.getItem('user')).permissions['view_savings'] },
        { name: 'Withdraws', number: 0, icon: 'mdi mdi-backup-restore', link: '/withdraws', visible: JSON.parse(localStorage.getItem('user')).permissions['view_withdraws'] },
        { name: 'Charges', number: 0, icon: 'mdi mdi-set-left', link: '/withdrawcharges', visible: JSON.parse(localStorage.getItem('user')).permissions['view_withdraw_charges'] },
        { name: 'Shares', number: 0, icon: 'mdi mdi-ungroup', link: '/shares', visible: JSON.parse(localStorage.getItem('user')).permissions['view_shares'] },
        { name: 'Capital', number: 0, icon: 'mdi mdi-ticket-percent', link: '/shares', visible: JSON.parse(localStorage.getItem('user')).permissions['view_shares'] },
        { name: 'Loans', number: 0, icon: 'mdi mdi-cash-multiple', link: '/runningloans', visible: JSON.parse(localStorage.getItem('user')).permissions['view_runningloans'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
        { name: 'Loans Taken', number: 0, icon: 'mdi mdi-cash', link: '/runningloans', visible: JSON.parse(localStorage.getItem('user')).permissions['view_runningloans'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
        { name: 'Payments', number: 0, icon: 'mdi mdi-cash-check', link: '/closedloans', visible: JSON.parse(localStorage.getItem('user')).permissions['view_closedloans'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
        { name: 'Loan Interest', number: 0, icon: 'mdi mdi-database', link: '/closedloans', visible: JSON.parse(localStorage.getItem('user')).permissions['view_closedloans'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
        { name: 'Arrears', number: 0, icon: 'mdi mdi-chart-timeline', link: '/loanarrears', visible: JSON.parse(localStorage.getItem('user')).permissions['view_loanarrears'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
        { name: 'Returns', number: 0, icon: 'mdi mdi-wrap', link: '/investmentreturns', visible: JSON.parse(localStorage.getItem('user')).permissions['view_investment_returns'] },
      ],
    };
  },
  async mounted() {
    this.dashboardanalysis();
    this.graphicalanalysis();
    this.fetchMembers();
    this.fetchShares();
    this.fetchWithdraws();
    this.fetchSavings();
    this.financialYears();
    await this.refreshUserData();
    if (this.userData.viewed_updates == 0) {
      if (!localStorage.getItem('reloadOnce')) {
        localStorage.setItem('reloadOnce', 'true');
        window.location.reload();
      } else {
        localStorage.removeItem('reloadOnce');
        this.$bvModal.show('updates');
      }
    }

  },
  computed: {
    ...memberComputed,
    ...savingComputed,
    ...shareComputed,
    ...withdrawComputed,
    ...runningloanComputed,
    ...loandisbursementComputed,
    arrowDirection() {
      return this.arrow_direction;
    },
    activeCards() {
      return this.cards.filter(card => card.visible)
    }
  },
  methods: {
    ...memberMethods,
    ...savingMethods,
    ...shareMethods,
    ...withdrawMethods,
    ...runningloanMethods,
    ...loandisbursementMethods,
    commas(x) { return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '0'; },

    // Deterministic accent color per KPI card, cycling through the palette
    cardColor(index) {
      return this.cardPalette[index % this.cardPalette.length];
    },
    cardColorSoft(index) {
      const hex = this.cardColor(index);
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, 0.12)`;
    },

    async refreshUserData() {
      await axios.get('/user').then((response) => {
        this.userData = response.data.user;
        localStorage.setItem("user", JSON.stringify(this.userData));
      });
    },

    seenUpdates() {
      const obj = {
        member_id: this.userData.member.member_id
      }
      axios.post('viewed-updates', obj)
      this.$bvModal.hide('updates');
      this.userData.viewed_updates = 1;
      localStorage.setItem("user", JSON.stringify(this.userData));
    },

    toggleArrowDirection() {
      this.arrow_direction = !this.arrow_direction;
    },

    async selectFinancialYear(id) {
      this.loader = true;
      const url = '/dashboardanalysis'
      let obj = { fyear: id }
      await axios.post(url, obj).then((response) => {
        this.financialYear = response.data.financial_year.name;
        this.members = response.data.members;
        this.savings = response.data.savings;
        this.withdraws = response.data.withdraws;
        this.withdrawcharges = response.data.withdrawcharges;
        this.shares = response.data.shares;
        this.capital = response.data.capital;
        this.loans = response.data.loans;
        this.loanstaken = response.data.loanstaken;
        this.loanspaid = response.data.loanspaid;
        this.interest = response.data.interest;
        this.arrears = response.data.arrears;
        this.returns = response.data.returns;
        let cards = [
          { name: 'Members', number: this.members, icon: 'mdi mdi-account-multiple', link: '/viewmembers', visible: this.userData.permissions['view_members'] },
          { name: 'Savings', number: this.savings, icon: 'mdi mdi-google-circles-group', link: '/savings', visible: this.userData.permissions['view_savings'] },
          { name: 'Withdraws', number: this.withdraws, icon: 'mdi mdi-backup-restore', link: '/withdraws', visible: this.userData.permissions['view_withdraws'] },
          { name: 'Charges', number: this.withdrawcharges, icon: 'mdi mdi-set-left', link: '/withdrawcharges', visible: this.userData.permissions['view_withdraw_charges'] },
          { name: 'Shares', number: this.shares, icon: 'mdi mdi-ungroup', link: '/shares', visible: this.userData.permissions['view_shares'] },
          { name: 'Capital', number: this.capital, icon: 'mdi mdi-ticket-percent', link: '/shares', visible: this.userData.permissions['view_shares'] },
          { name: 'Loans', number: this.loans, icon: 'mdi mdi-cash-multiple', link: '/runningloans', visible: this.userData.permissions['view_runningloans'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
          { name: 'Loans Taken', number: this.loanstaken, icon: 'mdi mdi-cash', link: '/runningloans', visible: this.userData.permissions['view_runningloans'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
          { name: 'Payments', number: this.loanspaid, icon: 'mdi mdi-cash-check', link: '/closedloans', visible: this.userData.permissions['view_closedloans'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
          { name: 'Loan Interest', number: this.interest, icon: 'mdi mdi-database', link: '/closedloans', visible: this.userData.permissions['view_closedloans'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
          { name: 'Arrears', number: this.arrears, icon: 'mdi mdi-chart-timeline', link: '/loanarrears', visible: this.userData.permissions['view_loanarrears'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
          { name: 'Returns', number: this.returns, icon: 'mdi mdi-wrap', link: '/investmentreturns', visible: this.userData.permissions['view_investment_returns'] },
        ]
        this.cards = cards;
        this.fetchLoanDisbursements();
        this.fetchRunningLoans();
      })

      this.loader = false;
    },

    async financialYears() {
      let url = `/financialyears`;
      await axios.get(url).then((response) => {
        this.financial_years = response.data.years;
      });
    },

    async dashboardanalysis() {
      let url = `/dashboardanalysis`;
      await axios.post(url).then((response) => {
        this.members = response.data.members;
        this.savings = response.data.savings;
        this.withdraws = response.data.withdraws;
        this.withdrawcharges = response.data.withdrawcharges;
        this.shares = response.data.shares;
        this.capital = response.data.capital;
        this.loans = response.data.loans;
        this.loanstaken = response.data.loanstaken;
        this.loanspaid = response.data.loanspaid;
        this.interest = response.data.interest;
        this.arrears = response.data.arrears;
        this.returns = response.data.returns;
        let cards = [
          { name: 'Members', number: this.members, icon: 'mdi mdi-account-multiple', link: '/viewmembers', visible: this.userData.permissions['view_members'] },
          { name: 'Savings', number: this.savings, icon: 'mdi mdi-google-circles-group', link: '/savings', visible: this.userData.permissions['view_savings'] },
          { name: 'Withdraws', number: this.withdraws, icon: 'mdi mdi-backup-restore', link: '/withdraws', visible: this.userData.permissions['view_withdraws'] },
          { name: 'Charges', number: this.withdrawcharges, icon: 'mdi mdi-set-left', link: '/withdrawcharges', visible: this.userData.permissions['view_withdraw_charges'] },
          { name: 'Shares', number: this.shares, icon: 'mdi mdi-ungroup', link: '/shares', visible: this.userData.permissions['view_shares'] },
          { name: 'Capital', number: this.capital, icon: 'mdi mdi-ticket-percent', link: '/shares', visible: this.userData.permissions['view_shares'] },
          { name: 'Loans', number: this.loans, icon: 'mdi mdi-cash-multiple', link: '/runningloans', visible: this.userData.permissions['view_runningloans'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
          { name: 'Loans Taken', number: this.loanstaken, icon: 'mdi mdi-cash', link: '/runningloans', visible: this.userData.permissions['view_runningloans'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
          { name: 'Payments', number: this.loanspaid, icon: 'mdi mdi-cash-check', link: '/closedloans', visible: this.userData.permissions['view_closedloans'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
          { name: 'Loan Interest', number: this.interest, icon: 'mdi mdi-database', link: '/closedloans', visible: this.userData.permissions['view_closedloans'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
          { name: 'Arrears', number: this.arrears, icon: 'mdi mdi-chart-timeline', link: '/loanarrears', visible: this.userData.permissions['view_loanarrears'] && JSON.parse(localStorage.getItem('user')).permissions['view_loans'] },
          { name: 'Returns', number: this.returns, icon: 'mdi mdi-wrap', link: '/investmentreturns', visible: this.userData.permissions['view_investment_returns'] },
        ]
        this.cards = cards;
        this.fetchLoanDisbursements();
        this.fetchRunningLoans();
      });
    },
    async graphicalanalysis() {
      let url = "/graphicalanalysis";
      await axios.get(url).then((response) => {
        this.yearlyloans = response.data.loans;
        this.yearlypayments = response.data.payments;
        this.yearlysavings = response.data.savings;
        this.month = response.data.month;
        this.year = response.data.year;
      });
      let LoanData = this.yearlyloans.map((e) => { return { x: e.month, y: e.amount, }; });
      let PaymentData = this.yearlypayments.map((e) => { return { x: e.month, y: e.amount, }; });
      let SavingsData = this.yearlysavings.map((e) => { return { x: e.name, y: e.amount }; });
      let WithdrawsData = this.yearlysavings.map((e) => { return { x: e.name, y: e.totals }; });
      this.monthlyloans.series = [
        { name: "Loans", data: LoanData },
        { name: "Payment", data: PaymentData }
      ];
      this.monthlysavings.series = [
        { name: "Savings", data: SavingsData },
        { name: "Withdraws", data: WithdrawsData }
      ];
    },

    nextUpdate() {
      if (this.update_number < this.update_total) {
        this.update_number += 1;
      }
    },

    previousUpdate() {
      if (this.update_number > 1) {
        this.update_number -= 1;
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500;600&display=swap');

.dash-root {
  --ink: #0B1220;
  --ink-soft: #64748B;
  --bg: #F5F7FA;
  --surface: #FFFFFF;
  --border: #E6E9EF;
  --accent: #0E7C66;
  --accent-soft: #E4F5F0;
  font-family: 'Inter', sans-serif;
  color: var(--ink);
}

/* ---------- Header ---------- */
.dash-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin: 4px 0 20px;
}

.dash-title {
  font-family: 'Sora', sans-serif;
  font-weight: 600;
  font-size: 1.15rem;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--ink);
}

.dash-title i {
  color: var(--accent);
}

.fy-pill {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 6px 8px 6px 16px;
}

.fy-pill--static {
  padding: 8px 18px;
}

.fy-label {
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--ink-soft);
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.fy-value {
  font-family: 'IBM Plex Mono', monospace;
  font-weight: 600;
  font-size: 0.85rem;
}

.fy-dropdown ::v-deep .btn {
  border-radius: 999px !important;
  font-family: 'IBM Plex Mono', monospace;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 6px 16px;
}

.fy-loading {
  padding: 0 6px;
}

/* ---------- KPI Grid ---------- */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 14px;
  margin-bottom: 10px;
}

.kpi-card {
  position: relative;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 15px 15px 14px;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(11, 18, 32, 0.08);
  border-color: rgba(14, 124, 102, 0.35);
}

.kpi-card__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
}

.kpi-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
}

.kpi-card__label {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--ink-soft);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1.3;
  max-width: 70%;
}

.kpi-card__icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  flex-shrink: 0;
}

.kpi-card__value {
  font-family: Arial, sans-serif;
  font-weight: 600;
  font-size: 1.35rem;
  letter-spacing: -0.01em;
  color: var(--ink);
}

/* ---------- Charts ---------- */
.chart-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 900px) {
  .chart-grid {
    grid-template-columns: 1fr;
  }
}

.chart-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 16px;
}

.chart-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.chart-card__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.chart-card__title {
  font-family: 'Sora', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  margin: 0;
  color: var(--ink);
}

/* ---------- No access state ---------- */
.no-access {
  max-width: 420px;
  margin: 60px auto;
  text-align: center;
  padding: 40px 24px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
}

.no-access__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 16px;
  border-radius: 12px;
  background: var(--accent-soft);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
}

.no-access__img {
  max-width: 140px;
  margin: 0 auto 16px;
  display: block;
}

.no-access__title {
  font-family: 'Sora', sans-serif;
  font-weight: 600;
  font-size: 1.05rem;
  margin-bottom: 6px;
}

.no-access__subtitle {
  font-size: 0.85rem;
  color: var(--ink-soft);
  line-height: 1.5;
}

/* ---------- Updates modal ---------- */
.updates-modal__title {
  font-family: 'Sora', sans-serif;
  font-weight: 600;
  font-size: 1.3rem;
  text-align: center;
  margin-bottom: 20px;
  color: var(--ink);
}

.updates-modal__body {
  display: flex;
  align-items: center;
  gap: 12px;
}

.updates-modal__frame {
  flex: 1;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  background: var(--surface);
}

.updates-modal__frame img {
  width: 100%;
  display: block;
}

.updates-nav {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
  cursor: pointer;
  transition: background 0.15s ease;
}

.updates-nav:hover:not(.is-disabled) {
  background: var(--accent-soft);
  color: var(--accent);
}

.updates-nav.is-disabled {
  opacity: 0.35;
  pointer-events: none;
}

.updates-modal__dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin: 16px 0 20px;
}

.updates-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--border);
  transition: all 0.2s ease;
}

.updates-dot.is-active {
  background: var(--accent);
  width: 18px;
  border-radius: 4px;
}

.updates-modal__footer {
  text-align: center;
}

.btn-primary-pill {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 0.9rem;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 10px 32px;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.btn-primary-pill:hover {
  opacity: 0.9;
}
</style>